// Authentication Context
// Manages user authentication state, organization, and role

import { User as FirebaseUser } from 'firebase/auth';
import { createContext, useContext } from 'react';
import type { Organization, User } from '../models';

interface AuthState {
  firebaseUser: FirebaseUser | null;
  user: User | null;
  organization: Organization | null;
  loading: boolean;
  error: string | null;
}

interface AuthContextType extends AuthState {
  isAuthenticated: boolean;
  isAdmin: boolean;
  organizationId: string | null;
  canUseLocalImpersonation: boolean;
  isLocalImpersonation: boolean;
  availableOrganizations: Organization[];
  availableLocalUsers: User[];
  selectedOrganizationId: string | null;
  setSelectedOrganizationId: (organizationId: string | null) => void;
  selectedLocalUserId: string | null;
  setSelectedLocalUserId: (userId: string | null) => void;
  selectedLocalRole: User['role'];
  setSelectedLocalRole: (role: User['role']) => void;
  refreshLocalImpersonationData: () => void;
}

const AuthContext = createContext<AuthContextType>({
  firebaseUser: null,
  user: null,
  organization: null,
  loading: true,
  error: null,
  isAuthenticated: false,
  isAdmin: false,
  organizationId: null,
  canUseLocalImpersonation: false,
  isLocalImpersonation: false,
  availableOrganizations: [],
  availableLocalUsers: [],
  selectedOrganizationId: null,
  setSelectedOrganizationId: () => {},
  selectedLocalUserId: null,
  setSelectedLocalUserId: () => {},
  selectedLocalRole: 'admin',
  setSelectedLocalRole: () => {},
  refreshLocalImpersonationData: () => {},
});

export const useAuthContext = () => useContext(AuthContext);

export { AuthContext };
